import React, {
  ReactElement, ReactNode, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useLocation } from 'react-router';
import Context, { ClientStatusContextType } from './ClientStatusContext';
import { ClientStatus } from '../../../model/ClientStatus';
import { useSuccessMessageContext } from '../../SuccessMessageContext';

interface Props {
  children: ReactNode;
}

const ClientStatusContextProvider = ({ children }: Props): ReactElement => {
  const [statusState, setStatusState] = useState<ClientStatus>();
  const [displayed, setDisplayed] = useState<boolean>(false);
  const currentLocation = useLocation();
  const { clearMessages } = useSuccessMessageContext();

  useEffect(() => {
    setDisplayed(true);
  }, [currentLocation]);

  const handleStatus = useCallback((status: ClientStatus) => {
    if (status.generalErrors.length !== 0 && clearMessages) {
      clearMessages();
    }
    setStatusState(status);
    setDisplayed(false);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    if (displayed) {
      setStatusState(null);
    }
  }, [displayed]);

  const clearStatus = (): void => {
    setStatusState(null);
  };

  const clearGeneralErrorMessage = useCallback((message: string) => {
    const updatedErrorMessageList = statusState.generalErrors
      .filter(currentMessage => currentMessage.message !== message);
    setStatusState(prevState => ({
      ...prevState,
      generalErrors: updatedErrorMessageList,
    }));
  }, [statusState?.generalErrors]);

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const value: ClientStatusContextType = useMemo(() => ({
    status: statusState,
    handleStatus,
    clearStatus,
    clearGeneralErrorMessage,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [statusState, clearGeneralErrorMessage]);

  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  );
};

export default ClientStatusContextProvider;
